import { useGraphQLQuery } from '@betterment/graphql';
import { useMatchMedia } from '@betterment/js-runtime';
import { Box, Button, H1 } from '@betterment/samba-react';
import { SambaAutoLink } from '@betterment/shared-components';

import { HeaderDocument } from './graphql/query.header.generated';

import { trackClick } from '../../features/track';
import { LegacyTransferType } from '../../graphql/schema';
import { TransactionButtonsTransactionOption } from '../../Shared/components/transaction-buttons/TransactionButtons';
import TransferOrRolloverDropdown from '../../Shared/components/TransferOrRolloverDropdown';
import { urlBuilder } from '../../Shared/urlBuilder';
import { browserTimeOfDay } from '../../stimulus/models/helpers';

export default function Header() {
  const isMediumScreen = useMatchMedia('atLeastMedium');
  const isLargeScreen = useMatchMedia('atLeastLarge');

  const { data } = useGraphQLQuery(HeaderDocument, { throwOnError: true });

  if (!data) return null;

  const userCommonFirstName = data.user.commonFirstName;
  const shouldShowReferAFriendCta = data.isReferralEligible;
  const hasTransferableGoals = data.envelopes && data.envelopes.length > 0;

  const timeOfDay = browserTimeOfDay();
  const greeting = `Good ${timeOfDay}, ${userCommonFirstName}`;

  const transactionOptions: TransactionButtonsTransactionOption[] = [
    // TODO: fetch transaction options from graphql
    // Only include transfer option if there are transferable goals
    ...(hasTransferableGoals
      ? [{ __typename: 'TransferOption' as const, id: 'transfer', legacyTransferType: LegacyTransferType.Transfer }]
      : []),
    // Always include these options in the header
    { __typename: 'CharitableDonationOption', id: 'donation' },
    { __typename: 'RolloverOption', id: 'rollover' },
    { __typename: 'TransferOption', id: 'withdrawal', legacyTransferType: LegacyTransferType.Withdrawal },
  ];

  if (!isMediumScreen && !hasTransferableGoals) {
    return null;
  }

  return (
    <Box
      data-testid="specPageHomeHeader"
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingY: {
          base: 'space.200',
          md: 'space.400',
        },
      }}
    >
      {isMediumScreen ? (
        <H1 css={{ flex: '1' }}>{greeting}</H1>
      ) : (
        <H1 variant="title200" css={{ paddingLeft: 'space.300' }}>
          {greeting}
        </H1>
      )}
      {isMediumScreen && (
        <Box
          css={{
            display: 'flex',
            alignItems: 'center',
            gap: 'space.100',
          }}
        >
          {shouldShowReferAFriendCta && (
            <SambaAutoLink
              variant="ghostSecondary"
              spacing="default"
              to={urlBuilder.appPersonalReferralInvites.absolutePath}
              onClick={() => trackClick({ name: 'ReferAFriend', module: 'HeaderNav' })}
            >
              <Button.Icon name="Present" />
              Refer a friend
            </SambaAutoLink>
          )}

          {isLargeScreen && (
            <>
              <TransferOrRolloverDropdown transactionOptions={transactionOptions} />
              <SambaAutoLink
                variant="solidPrimary"
                spacing="default"
                to={urlBuilder.appDepositSetup.absolute()}
                onClick={() => trackClick({ name: 'Deposit', module: 'HeaderNav' })}
              >
                Deposit
              </SambaAutoLink>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
